import React, { useState, useEffect, useRef } from 'react';
import { 
  Box, Button, Typography, Table, TableBody, TableCell, 
  TableContainer, TableHead, TableRow, Paper, IconButton, 
  Dialog, DialogTitle, DialogContent, DialogActions, TextField,
  Switch, FormControlLabel, Tooltip, Stack, Chip 
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import FormatBoldIcon from '@mui/icons-material/FormatBold';
import FormatItalicIcon from '@mui/icons-material/FormatItalic';
import FormatUnderlinedIcon from '@mui/icons-material/FormatUnderlined';
import FormatQuoteIcon from '@mui/icons-material/FormatQuote';
import FormatListBulletedIcon from '@mui/icons-material/FormatListBulleted';
import LinkIcon from '@mui/icons-material/Link';
import HighlightIcon from '@mui/icons-material/Highlight';
import AddIcon from '@mui/icons-material/Add';
import { API_BASE_URL, UPLOADS_BASE_URL } from '../../context/AuthContext';

const PASTEL_PRESETS = [
  { hex: '#D4BBDD', name: 'Lilla Salvia' },
  { hex: '#F7E951', name: 'Giallo Sole' },
  { hex: '#F6E6E8', name: 'Rosa Cipria' },
  { hex: '#E3EEDD', name: 'Verde Salvia' },
  { hex: '#E6F0FA', name: 'Azzurro Polvere' },
  { hex: '#FFF1E6', name: 'Pesca Chiaro' },
  { hex: '#FFFFFF', name: 'Bianco' }
];

const resolveImageUrl = (url) => {
  if (!url) return '';
  if (url.startsWith('http') || url.startsWith('data:')) return url;
  if (url.startsWith('images/') || url.startsWith('/images/')) {
    return url.startsWith('/') ? url : `/${url}`;
  }
  return `${UPLOADS_BASE_URL}/${url.replace(/^\//, '')}`;
};

export default function HomeAdmin() {
  const [cards, setCards] = useState([]);
  const [openForm, setOpenForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [uploading, setUploading] = useState(false);
  const textAreaRef = useRef(null);

  const [formData, setFormData] = useState({
    title: '',
    text_content: '',
    image_url: '',
    bg_color: '#D4BBDD',
    is_full_width: false
  });

  const fetchCards = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/home_cards.php`);
      const data = await response.json();
      if (Array.isArray(data)) {
        setCards(data);
      }
    } catch (error) {
      console.error("Errore recupero card della Home:", error);
    }
  };

  useEffect(() => {
    fetchCards();
  }, []);

  const handleOpenForm = (card = null) => {
    if (card) {
      setEditingId(card.id);
      setFormData({
        title: card.title || '',
        text_content: card.text_content || '',
        image_url: card.image_url || '',
        bg_color: card.bg_color || '#D4BBDD',
        is_full_width: Boolean(Number(card.is_full_width))
      });
    } else {
      setEditingId(null);
      setFormData({
        title: '',
        text_content: '',
        image_url: '',
        bg_color: '#D4BBDD',
        is_full_width: false
      });
    }
    setOpenForm(true);
  };

  const handleCloseForm = () => {
    setOpenForm(false);
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  // Caricamento foto della Card
  const handleUploadImage = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const token = localStorage.getItem('adminToken');
    const formDataUpload = new FormData();
    formDataUpload.append('image', file);

    setUploading(true);
    try {
      const response = await fetch(`${API_BASE_URL}/upload_home_image.php`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
        body: formDataUpload
      });
      const data = await response.json();
      if (response.ok && data.url) {
        setFormData(prev => ({ ...prev, image_url: data.url }));
      } else {
        alert(data.error || "Errore durante il caricamento dell'immagine.");
      }
    } catch (error) {
      console.error("Home image upload error:", error);
    } finally {
      setUploading(false);
      e.target.value = '';
    }
  };

  // Funzione per applicare i tag di formattazione al testo selezionato
  const applyFormatting = (openTag, closeTag, defaultText = '') => {
    const textarea = textAreaRef.current;
    if (!textarea) return;

    const start = textarea.selectionStart;
    const end = textarea.selectionEnd;
    const currentText = formData.text_content;
    const selectedText = currentText.substring(start, end) || defaultText;

    const newText = currentText.substring(0, start) + openTag + selectedText + closeTag + currentText.substring(end);
    setFormData(prev => ({ ...prev, text_content: newText }));

    // Ripristina la selezione del testo aggiornato
    setTimeout(() => {
      textarea.focus();
      textarea.setSelectionRange(start + openTag.length, start + openTag.length + selectedText.length);
    }, 0);
  };

  const handleSave = async () => {
    if (!formData.text_content.trim()) {
      alert("Il testo della Card è obbligatorio.");
      return;
    }

    const token = localStorage.getItem('adminToken');
    const method = editingId ? 'PUT' : 'POST';
    const payload = editingId ? { ...formData, id: editingId } : formData;

    try {
      const response = await fetch(`${API_BASE_URL}/home_cards.php`, {
        method: method,
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(payload)
      });

      if (response.ok) {
        handleCloseForm();
        fetchCards();
      } else {
        const data = await response.json();
        alert(data.error || "Errore durante il salvataggio della Card.");
      }
    } catch (error) {
      console.error("Errore salvataggio Card:", error);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Sei sicuro di voler eliminare questa Card dalla Home Page?")) return;

    const token = localStorage.getItem('adminToken');
    try {
      const response = await fetch(`${API_BASE_URL}/home_cards.php`, {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ id })
      });

      if (response.ok) {
        fetchCards();
      } else {
        alert("Errore durante l'eliminazione della Card.");
      }
    } catch (error) {
      console.error("Errore eliminazione Card:", error);
    }
  };

  const handleMoveOrder = async (index, direction) => {
    const newCards = [...cards];
    const targetIndex = index + direction;
    if (targetIndex < 0 || targetIndex >= newCards.length) return;

    // Scambia di posto
    const temp = newCards[index];
    newCards[index] = newCards[targetIndex];
    newCards[targetIndex] = temp;

    // Ricalcola card_order
    const updatedCards = newCards.map((card, i) => ({
      ...card,
      card_order: i + 1
    }));

    setCards(updatedCards);

    // Salva il nuovo ordine sul server
    const token = localStorage.getItem('adminToken');
    try {
      await fetch(`${API_BASE_URL}/home_cards.php`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(updatedCards.map(c => ({ id: c.id, card_order: c.card_order })))
      });
    } catch (err) {
      console.error("Errore aggiornamento ordine card:", err);
    }
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h6">Gestione Card della Home Page</Typography>
        <Tooltip title="Aggiungi una nuova Card personalizzata nella Home Page">
          <Button 
            variant="contained" 
            startIcon={<AddIcon />}
            onClick={() => handleOpenForm()}
          >
            Nuova Card
          </Button>
        </Tooltip>
      </Box>

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow sx={{ bgcolor: '#f5f5f5' }}>
              <TableCell width={70} align="center">Ordine</TableCell>
              <TableCell width={80}>Anteprima</TableCell>
              <TableCell>Titolo / Contenuto</TableCell>
              <TableCell width={120}>Colore Sfondo</TableCell>
              <TableCell width={120}>Formato</TableCell>
              <TableCell align="right" width={180}>Azioni</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {cards.map((row, index) => (
              <TableRow key={row.id}>
                <TableCell align="center">
                  <Stack direction="row" spacing={0.5} justifyContent="center">
                    <Tooltip title="Sposta in alto">
                      <span>
                        <IconButton 
                          size="small" 
                          disabled={index === 0} 
                          onClick={() => handleMoveOrder(index, -1)}
                        >
                          <ArrowUpwardIcon fontSize="small" />
                        </IconButton>
                      </span>
                    </Tooltip>
                    <Tooltip title="Sposta in basso">
                      <span>
                        <IconButton 
                          size="small" 
                          disabled={index === cards.length - 1} 
                          onClick={() => handleMoveOrder(index, 1)}
                        >
                          <ArrowDownwardIcon fontSize="small" />
                        </IconButton>
                      </span>
                    </Tooltip>
                  </Stack>
                </TableCell>

                <TableCell>
                  {row.image_url ? (
                    <img 
                      src={resolveImageUrl(row.image_url)} 
                      alt="" 
                      style={{ width: 50, height: 50, objectFit: 'cover', borderRadius: 6 }} 
                    />
                  ) : (
                    <Typography variant="caption" color="text.secondary">Senza foto</Typography>
                  )}
                </TableCell>

                <TableCell>
                  <Typography variant="subtitle2" sx={{ fontWeight: 'bold' }}>
                    {row.title || <i>(Senza Titolo)</i>}
                  </Typography>
                  <Typography 
                    variant="caption" 
                    color="text.secondary"
                    sx={{ 
                      display: '-webkit-box', 
                      WebkitLineClamp: 2, 
                      WebkitBoxOrient: 'vertical', 
                      overflow: 'hidden' 
                    }}
                  >
                    {row.text_content.replace(/<[^>]*>?/gm, '')}
                  </Typography>
                </TableCell>

                <TableCell>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Box 
                      sx={{ 
                        width: 24, 
                        height: 24, 
                        borderRadius: '50%', 
                        bgcolor: row.bg_color || '#D4BBDD',
                        border: '1px solid #ccc'
                      }} 
                    />
                  </Box>
                </TableCell>

                <TableCell>
                  {Number(row.is_full_width) === 1 ? (
                    <Chip label="Larghezza Intera" size="small" color="primary" variant="outlined" />
                  ) : (
                    <Chip label="Griglia" size="small" color="default" variant="outlined" />
                  )}
                </TableCell>

                <TableCell align="right">
                  <Tooltip title="Modifica Card">
                    <IconButton color="primary" onClick={() => handleOpenForm(row)}>
                      <EditIcon />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Elimina Card">
                    <IconButton color="error" onClick={() => handleDelete(row.id)}>
                      <DeleteIcon />
                    </IconButton>
                  </Tooltip>
                </TableCell>
              </TableRow>
            ))}
            {cards.length === 0 && (
              <TableRow>
                <TableCell colSpan={6} align="center">Nessuna Card presente in Home Page.</TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Modale Form Creazione / Modifica */}
      <Dialog open={openForm} onClose={handleCloseForm} fullWidth maxWidth="md">
        <DialogTitle>{editingId ? 'Modifica Card Home Page' : 'Nuova Card Home Page'}</DialogTitle>
        <DialogContent>
          <Box component="form" sx={{ mt: 1 }}>
            
            <FormControlLabel
              control={
                <Switch 
                  checked={formData.is_full_width} 
                  onChange={handleChange} 
                  name="is_full_width" 
                />
              }
              label="Mostra a Larghezza Intera (banner introduttivo in alto)"
            />

            <TextField
              margin="normal" fullWidth
              label="Titolo Card (opzionale)"
              name="title"
              value={formData.title}
              onChange={handleChange}
              placeholder="Es. LA SEDE, L’INSEGNANTE, LO YOGA..."
            />

            {/* Toolbar di Formattazione Testo */}
            <Box sx={{ mt: 2, mb: 1 }}>
              <Typography variant="caption" sx={{ fontWeight: 600, display: 'block', mb: 0.5, color: 'text.secondary' }}>
                Strumenti di formattazione testo:
              </Typography>
              <Paper variant="outlined" sx={{ p: 0.5, display: 'flex', gap: 0.5, flexWrap: 'wrap', bgcolor: '#f9f9f9' }}>
                <Tooltip title="Grassetto">
                  <IconButton size="small" onClick={() => applyFormatting('<b>', '</b>')}>
                    <FormatBoldIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
                <Tooltip title="Corsivo">
                  <IconButton size="small" onClick={() => applyFormatting('<i>', '</i>')}>
                    <FormatItalicIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
                <Tooltip title="Sottolineato">
                  <IconButton size="small" onClick={() => applyFormatting('<u>', '</u>')}>
                    <FormatUnderlinedIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
                <Tooltip title="Evidenziato">
                  <IconButton size="small" onClick={() => applyFormatting('<mark>', '</mark>')}>
                    <HighlightIcon fontSize="small" color="action" />
                  </IconButton>
                </Tooltip>
                <Tooltip title="Citazione">
                  <IconButton size="small" onClick={() => applyFormatting('<blockquote>"', '"<br/>— Nome Autore</blockquote>')}>
                    <FormatQuoteIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
                <Tooltip title="Elenco Puntato">
                  <IconButton size="small" onClick={() => applyFormatting('<ul>\n  <li>', '</li>\n</ul>')}>
                    <FormatListBulletedIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
                <Tooltip title="Inserisci Link">
                  <IconButton size="small" onClick={() => {
                    const url = prompt("Inserisci l'indirizzo web (URL):", "https://");
                    if (url) {
                      applyFormatting(`<a href="${url}" target="_blank" rel="noopener noreferrer">`, '</a>', 'Testo Link');
                    }
                  }}>
                    <LinkIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              </Paper>
            </Box>

            <TextField
              inputRef={textAreaRef}
              margin="normal" fullWidth required
              label="Testo della Card (obbligatorio)"
              name="text_content"
              multiline rows={7}
              value={formData.text_content}
              onChange={handleChange}
              placeholder="Inserisci qui il testo descrittivo. Puoi usare i pulsanti in alto per formattare il testo."
            />

            {/* Immagine Opzionale */}
            <Box sx={{ mt: 2, mb: 2 }}>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                Immagine della Card (opzionale):
              </Typography>
              {formData.image_url ? (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, p: 1.5, border: '1px solid #e0e0e0', borderRadius: 1, bgcolor: '#f9f9f9' }}>
                  <img 
                    src={resolveImageUrl(formData.image_url)} 
                    alt="Anteprima" 
                    style={{ width: 50, height: 50, objectFit: 'cover', borderRadius: 4 }}
                  />
                  <Typography variant="caption" sx={{ flexGrow: 1, wordBreak: 'break-all' }}>
                    {formData.image_url}
                  </Typography>
                  <Button 
                    size="small" 
                    color="error" 
                    onClick={() => setFormData(prev => ({ ...prev, image_url: '' }))}
                  >
                    Rimuovi
                  </Button>
                </Box>
              ) : (
                <Tooltip title="Seleziona un'immagine per questa Card dal tuo computer">
                  <Button 
                    variant="outlined" 
                    startIcon={<UploadFileIcon />}
                    component="label"
                    disabled={uploading}
                  >
                    {uploading ? 'Caricamento immagine...' : '+ Carica Immagine'}
                    <input 
                      type="file" 
                      accept="image/*"
                      hidden
                      onChange={handleUploadImage} 
                    />
                  </Button>
                </Tooltip>
              )}
            </Box>

            {/* Sfondo Card */}
            <Box sx={{ mt: 2 }}>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                Colore di Sfondo della Card:
              </Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5, alignItems: 'center' }}>
                {/* Cerchio Scelta Libera */}
                <Tooltip title="Colore su misura: clicca per scegliere qualsiasi colore dal tavolozza">
                  <Box sx={{ position: 'relative', width: 36, height: 36, cursor: 'pointer' }}>
                    <input 
                      type="color" 
                      value={formData.bg_color} 
                      onChange={(e) => setFormData(prev => ({ ...prev, bg_color: e.target.value }))}
                      style={{
                        position: 'absolute', top: 0, left: 0, width: '100%', height: '100%',
                        opacity: 0, cursor: 'pointer', zIndex: 2
                      }}
                    />
                    <Box 
                      sx={{
                        width: 36, height: 36, borderRadius: '50%',
                        backgroundColor: formData.bg_color,
                        border: '2px dashed #999',
                        display: 'flex', alignItems: 'center', justifyContent: 'center'
                      }}
                    >
                      🎨
                    </Box>
                  </Box>
                </Tooltip>

                {/* Preset Pastello */}
                {PASTEL_PRESETS.map((preset) => {
                  const isSelected = formData.bg_color.toLowerCase() === preset.hex.toLowerCase();
                  return (
                    <Tooltip key={preset.hex} title={preset.name}>
                      <Box
                        onClick={() => setFormData(prev => ({ ...prev, bg_color: preset.hex }))}
                        sx={{
                          width: 36,
                          height: 36,
                          borderRadius: '50%',
                          backgroundColor: preset.hex,
                          border: isSelected ? '3px solid #1976d2' : '1px solid #ccc',
                          boxShadow: isSelected ? '0 0 8px rgba(25, 118, 210, 0.5)' : 'none',
                          cursor: 'pointer',
                          transition: 'transform 0.15s ease-in-out',
                          '&:hover': { transform: 'scale(1.15)' }
                        }}
                      />
                    </Tooltip>
                  );
                })}
              </Box>
            </Box>

          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 2.5 }}>
          <Button onClick={handleCloseForm}>Annulla</Button>
          <Button onClick={handleSave} variant="contained" color="primary">Salva Card</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
